<template>
  <div class="bdc_main_wrap whero">
    <div class="thw_content">
      <div class="internal-sub-nav">
        <div
          v-for="(tab, i) in tabOptions"
          :key="i"
          class="isn-item"
          :class="{ active: tab.id === selectedTab, disabled: tab.disabled }"
          @click="tabOptions.length === 1 ? null : onTabClick(tab)"
        >
          {{ tab.value }}
        </div>
      </div>
    </div>
    <div
      v-if="tableauJwtToken && selectedTabHandlerType === 'tableauReportHandler'"
      class="bdc_top_panel_wrap"
    >
      <tableau-viz
        id="tableauViz"
        ref="tableauContainer"
        :src="tableauViewURL"
        :token="tableauJwtToken"
        :on-viz-load-error="handleTableauError"
        toolbar="top"
        hide-tabs
      >
        <custom-parameter name=":showShareOptions" value="false"></custom-parameter>
        <custom-parameter name=":customViews" value="false"></custom-parameter>
        <viz-filter field="Advertiser" :value="dspAdvertiserIds"> </viz-filter>
        <viz-filter field="Start Date" :value="startDate"></viz-filter>
        <viz-filter field="End Date" :value="endDate"></viz-filter>
        <viz-filter
          v-if="addDaysToConvertParam"
          field="Days To Convert Parameter"
          :value="updateConversionWindow"
        ></viz-filter>

        <viz-filter
          v-if="addCampaignIdParam"
          field="Campaign ID Parameter"
          :value="campaignIdParameter"
        ></viz-filter>
        <!-- Adready ID Parameter is from the Tableau Template Parameter Name -->
        <viz-filter
          v-if="addAdreadyIdParam"
          field="AdReady ID Parameter"
          :value="placementIdParameter"
        ></viz-filter>
        <viz-filter
          v-if="addEventAndEventCategory"
          field="Event Category Parameter"
          :value="eventFilters.eventCategory"
        ></viz-filter>
        <viz-filter
          v-if="addEventAndEventCategory && eventFilters.eventSubcategory != null"
          field="Event Subcategory Parameter"
          :value="eventFilters.eventSubcategory"
        ></viz-filter>
        <viz-filter
          v-if="addMediaTypeParam"
          field="Media Type Parameter"
          :value="mediaTypeParameter"
        ></viz-filter>
        <viz-filter
          v-if="addCreativeIdParam"
          field="Creative ID Parameter"
          :value="creativeIdParameter"
        ></viz-filter>
        <viz-filter
          v-if="addConversionTypeParam"
          field="Conversion Type Parameter"
          :value="conversionTypeParameter"
        ></viz-filter>

        <viz-filter field=":refresh" value="yes"></viz-filter>
        <viz-parameter field="embed" value="yes"></viz-parameter>
      </tableau-viz>
    </div>
    <div v-else-if="selectedTabHandlerType === 'incrementalityHandler'">
      <incrementality class="chart-sec" />
    </div>
    <div v-else-if="!tableauJwtToken && !loading">
      Something went wrong. Please contact Digital Remedy technical support.
    </div>
  </div>
</template>
<script>
import _ from 'underscore';
import { isBlank } from 'adready-api/helpers/common';
import { get } from 'vuex-pathify';
import { TableauEventType } from '@tableau/embedding-api';
import {
  MNI_REPORTING_TABLEAU_URL,
  DEFAULT_CONVERSION_WINDOW,
  MNI_OVERVIEW_TAB_OPTIONS,
  REACH_AND_FREQUENCY_TAB_OPTIONS,
  TABLEAU_PROD_URL,
  TABLEAU_DEV_URL,
  REACH_FREQ_DASHBOARD_FEATURE,
} from '~/constant';
import { formatDateForAPI } from '~/util/apiDateFormat';
import config from '~/config';
import { isDemoInstance, checkUserHasAiAssistant } from '~/util/utility-functions';
import EventBus from '../adready-vue/helpers/global/event-bus';
import tableauMixin from '~/components/mixins/tableau-mixin';

export default {
  components: {
    Incrementality: () =>
      import(
        /* webpackChunkName: "incrementality" */ '~/components/dash-panels/graphs/incrementality.vue'
      ),
  },
  mixins: [tableauMixin],
  props: {
    sectionTitle: {
      required: false,
      default: () => '',
      type: String,
    },
  },
  data() {
    const tabOptions = MNI_OVERVIEW_TAB_OPTIONS;
    let selectedTab = '';
    let selectedTabObject = null;
    let selectedTabTitle = '';
    let selectedTabUrl = '';
    let selectedTabSubUrl = '';
    let selectedTabDataUrl = '';
    if (tabOptions?.length) {
      const defaultSelectedTab = tabOptions.find((t) => t.selected) || this.tabOptions[0];
      selectedTab = defaultSelectedTab?.id || tabOptions[0].id;
      selectedTabObject = defaultSelectedTab || tabOptions[0];
      this.$store.set('dashboard/selectedDashboardTab', selectedTab);

      selectedTabTitle = defaultSelectedTab?.value || tabOptions[0].value;
      if (isDemoInstance()) {
        selectedTabUrl = defaultSelectedTab?.demoViewUrl || tabOptions[0].demoViewUrl;
        selectedTabSubUrl = defaultSelectedTab?.demoViewSubUrl || tabOptions[0].demoViewSubUrl;
        if (config.TABLEAU_URL === TABLEAU_PROD_URL) {
          selectedTabDataUrl =
            defaultSelectedTab?.demoDataUrlsProd || tabOptions[0].demoDataUrlsProd;
        } else if (config.TABLEAU_URL === TABLEAU_DEV_URL) {
          selectedTabDataUrl = defaultSelectedTab?.demoDataUrlsDev || tabOptions[0].demoDataUrlsDev;
        }
      } else {
        selectedTabUrl = defaultSelectedTab?.viewUrl || tabOptions[0].viewUrl;
        selectedTabSubUrl = defaultSelectedTab?.viewSubUrl || tabOptions[0].viewSubUrl;
        if (config.TABLEAU_URL === TABLEAU_PROD_URL) {
          selectedTabDataUrl = defaultSelectedTab?.dataUrlsProd || tabOptions[0].dataUrlsProd;
        } else if (config.TABLEAU_URL === TABLEAU_DEV_URL) {
          selectedTabDataUrl = defaultSelectedTab?.dataUrlsDev || tabOptions[0].dataUrlsDev;
        }
      }
    }
    return {
      tabOptions,
      selectedTab,
      selectedTabObject,
      selectedTabTitle,
      selectedTabUrl,
      selectedTabSubUrl,
      selectedTabDataUrl,
      MNI_REPORTING_TABLEAU_URL,
      tableauJwtToken: null,
      tableauError: false,
      sheets: [],
      selectedSheet: '',
      viz: null,
      container: 'tableau-viz',
      tableauRestToken: null,
      isTableauDashboardInitialized: false,
      isCachedDateLoaded: false,
      isMediaTypeOptionsInitialized: false,
      isCampaignOptionsInitialized: false,
      isAudienceOptionsInitialized: false,
      isCreativeOptionsInitialized: false,
      isEventOptionsInitialized: false,
      isComponentMounted: false,
      tableauDataParams: [],
      oldTableauDataParams: [],
      selectedTabHandlerType: 'tableauReportHandler',
      loading: false,
      isInitialClick: true,
      dspAdvertiserIds: [],
    };
  },
  computed: {
    account: get('common/account'),
    advertiser: get('common/advertiser'),
    dates: get('dashboard/dates'),
    campaignOptions: get('dashboard/filters@campaignOptions'),
    mediaTypeOptions: get('dashboard/filters@mediaTypeOptions'),
    conversionWindow: get('dashboard/filters@conversionWindow'),
    conversionTypeOptions: get('dashboard/filters@conversionTypeOptions'),
    adGroupOptions: get('dashboard/filters@adGroupOptions'),
    filtersApplied: get('dashboard/filtersApplied'),
    audienceOptions: get('dashboard/filters@audienceOptions'),
    creativeOptions: get('dashboard/filters@creativeOptions'),
    eventOptions: get('dashboard/filters@eventOptions'),
    currentUser: get('common/currentUser'),
    universalPixelId: get('common/universalPixelId'),

    addEventAndEventCategory() {
      if (
        [
          'reachAndFrequencyOverview',
          'reachAndFrequencyPublisherInsights',
          'reachAndFrequencyAudienceInsights',
        ].includes(this.selectedTab)
      ) {
        return false;
      }
      return true;
    },

    advertiserId() {
      return this.advertiser ? this.advertiser.xandrAdvertiserId : 0;
    },
    startDate() {
      return formatDateForAPI(this.dates.startDate);
    },
    endDate() {
      return formatDateForAPI(this.dates.endDate);
    },
    tableauViewURL() {
      const templateURL = `${config.TABLEAU_URL}${this.selectedTabUrl}`;
      let tableauViewURL = '';
      if (
        [
          'journeyanalytics',
          'reachAndFrequencyOverview',
          'reachAndFrequencyPublisherInsights',
          'reachAndFrequencyAudienceInsights',
        ].includes(this.selectedTab)
      ) {
        tableauViewURL = `${templateURL}`;
      } else {
        tableauViewURL = `${templateURL}${this.selectedTabSubUrl}`;
      }
      return tableauViewURL;
    },
    // below code return empty string when all items are selected sending empty string considered as all selected in tableau.
    selectedMediaTypeOptions() {
      const allChecked = this.mediaTypeOptions.every((item) => item.checked === true);

      if (allChecked) {
        return ''; // If all items are checked, return an empty string
      }

      return this.mediaTypeOptions
        .filter((item) => item.checked === true)
        .map((item) => item.value)
        .join(',');
    },
    campaignOptionsValues() {
      const allChecked = this.campaignOptions.every((f) => f?.checked === true);
      if (allChecked) {
        return ''; // If all items are checked, return an empty string
      }
      const result = this.campaignOptions.filter((f) => f?.checked);
      const campaignIds =
        result
          .map((res) => res.key)
          .filter((fres) => fres)
          .join(',') || '';
      return campaignIds;
    },
    selectedAudienceOptions() {
      const allChecked = this.audienceOptions.every((item) => item.checked === true);

      if (allChecked) {
        return ''; // If all items are checked, return an empty string
      }

      const selectedAudience = this.audienceOptions
        .filter((item) => item.checked === true)
        .map((item) => item.key)
        .join(',');

      return selectedAudience;
    },
    updateConversionWindow() {
      return this.conversionWindow ? this.conversionWindow : DEFAULT_CONVERSION_WINDOW;
    },
    creativeOptionsOptions() {
      const allChecked = this.creativeOptions.every((item) => item.checked === true);

      if (allChecked) {
        return ''; // If all items are checked, return an empty string
      }

      const selectedCreative = this.creativeOptions
        .filter((item) => item.checked === true)
        .map((item) => item.key)
        .join(',');

      return selectedCreative;
    },
    eventFilters() {
      const selectedEvent = this.eventOptions.filter((event) => event.selected === true);
      let eventCategory = [];
      let eventSubcategory = [];

      if (selectedEvent.length > 0) {
        // Collect parent categories
        const category = selectedEvent.map((event) => event.category);
        eventCategory = category.filter((fres) => fres).join('\\,') || '';

        // Filter out events that have subcategories and are selected
        const eventsWithChilds = selectedEvent.filter(
          (event) => event.numSubCategories > 0 && event.children
        );

        // Collect subcategories
        const subcategory = eventsWithChilds.flatMap(
          (event) =>
            event.children
              .filter((subEve) => subEve.selected) // Filter based on the selected property
              .map((subEve) => subEve.category) // Map to return only the category
        );

        if (isBlank(subcategory)) {
          eventSubcategory = null;
        } else {
          eventSubcategory = subcategory.filter((fres) => fres).join('\\,') || '';
        }

        // Check if all parent and child items are selected
        const allParentsSelected = this.eventOptions.every((event) => event.selected === true);
        const allChildrenSelected = this.eventOptions.every((event) =>
          event.children ? event.children.every((child) => child.selected === true) : true
        );

        // If all items are selected, both arrays should be empty
        if (allParentsSelected && allChildrenSelected) {
          eventCategory = [];
          eventSubcategory = [];
        }
      }
      return {
        eventCategory,
        eventSubcategory,
      };
    },
    campaignIdParameter() {
      const { campaignOptionsValues } = this;
      if (!campaignOptionsValues?.length) {
        return '';
      }

      const allChecked = this.campaignOptions.every((f) => f?.checked === true);
      if (allChecked) {
        return ''; // If all items are checked, return an empty string
      }
      const result = this.campaignOptions.filter((f) => f?.checked);
      const campaignIds =
        result
          .map((res) => res.key)
          .filter((fres) => fres)
          .join('\\,') || '';
      return campaignIds;
    },
    conversionTypeParameter() {
      const conversionTypeValues = this.conversionTypeOptions.filter((item) => {
        return item.selected;
      });
      if (!conversionTypeValues?.length) {
        return '';
      }

      const allSelected = this.conversionTypeOptions.every((f) => f?.selected === true);
      if (allSelected) {
        return ''; // If all items are selected, return an empty string
      }
      const result = this.conversionTypeOptions.filter((f) => f?.selected);
      const conversionTypes =
        result
          .map((res) => res.key)
          .filter((fres) => fres)
          .join('\\,') || '';
      return conversionTypes;
    },
    placementIdParameter() {
      const allChecked = this.audienceOptions.every((f) => f?.checked === true);
      if (allChecked) {
        return ''; // If all items are checked, return an empty string
      }
      const result = this.audienceOptions.filter((f) => f?.checked);
      const placementIds =
        result
          .map((res) => res.key)
          .filter((fres) => fres)
          .join('\\,') || '';
      return placementIds;
    },
    mediaTypeParameter() {
      const allChecked = this.mediaTypeOptions.every((item) => item.checked === true);
      if (allChecked) {
        return ''; // If all items are checked, return an empty string
      }
      const result = this.mediaTypeOptions.filter((item) => item.checked === true);
      const mediaTypeIds = result.map((item) => item.value).join('\\,') || '';
      return mediaTypeIds;
    },
    creativeIdParameter() {
      const allChecked = this.creativeOptions.every((f) => f?.checked === true);
      if (allChecked) {
        return ''; // If all items are checked, return an empty string
      }
      const result = this.creativeOptions.filter((f) => f?.checked);
      const creativeIds =
        result
          .map((res) => res.key)
          .filter((fres) => fres)
          .join('\\,') || '';
      return creativeIds;
    },
    hasAIAssistantAccess() {
      return checkUserHasAiAssistant(this.currentUser);
    },
    addDaysToConvertParam() {
      if (
        [
          'reachAndFrequencyOverview',
          'reachAndFrequencyPublisherInsights',
          'reachAndFrequencyAudienceInsights',
        ].includes(this.selectedTab)
      ) {
        return false;
      }
      return true;
    },
    addCampaignIdParam() {
      if (
        [
          'reachAndFrequencyOverview',
          'reachAndFrequencyPublisherInsights',
          'reachAndFrequencyAudienceInsights',
        ].includes(this.selectedTab)
      ) {
        return false;
      }
      return true;
    },
    addAdreadyIdParam() {
      if (
        [
          'reachAndFrequencyOverview',
          'reachAndFrequencyPublisherInsights',
          'reachAndFrequencyAudienceInsights',
        ].includes(this.selectedTab)
      ) {
        return false;
      }
      return true;
    },
    addMediaTypeParam() {
      if (
        [
          'journeyanalytics',
          'reachAndFrequencyOverview',
          'reachAndFrequencyPublisherInsights',
          'reachAndFrequencyAudienceInsights',
        ].includes(this.selectedTab)
      ) {
        return false;
      }
      return true;
    },
    addCreativeIdParam() {
      if (
        [
          'journeyanalytics',
          'reachAndFrequencyOverview',
          'reachAndFrequencyPublisherInsights',
          'reachAndFrequencyAudienceInsights',
        ].includes(this.selectedTab)
      ) {
        return false;
      }
      return true;
    },
    addConversionTypeParam() {
      if (
        [
          'journeyanalytics',
          'reachAndFrequencyOverview',
          'reachAndFrequencyPublisherInsights',
          'reachAndFrequencyAudienceInsights',
        ].includes(this.selectedTab)
      ) {
        return false;
      }
      return true;
    },
  },
  watch: {
    advertiserId: {
      immediate: false,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          if (o) {
            await this.debounceTableauAuthToken();
            if (this.hasAIAssistantAccess) await this.fetchAllTableauData();
          }
        }
      },
    },
    accountId: {
      immediate: false,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          if (o) {
            await this.debounceTableauAuthToken();
            if (this.hasAIAssistantAccess) await this.fetchAllTableauData();
          }
        }
      },
    },
    dates: {
      immediate: false,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          if (o) {
            if (this.isCachedDateLoaded && Object.hasOwn(n, 'compareStartDate')) {
              await this.debounceTableauAuthToken();
              if (this.hasAIAssistantAccess) await this.fetchAllTableauData();
            }
            if (!Object.hasOwn(n, 'compareStartDate')) {
              this.isCachedDateLoaded = true;
            }
            if (!this.isCachedDateLoaded && Object.hasOwn(n, 'compareStartDate')) {
              await this.debounceTableauAuthToken();
              if (this.hasAIAssistantAccess) await this.fetchAllTableauData();
            }
          }
        }
      },
    },
    mediaTypeOptions: {
      immediate: false,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          if (this.isMediaTypeOptionsInitialized) {
            await this.debounceTableauAuthToken();
            if (this.hasAIAssistantAccess) await this.fetchAllTableauData();
          }
          if (o.length) {
            this.isMediaTypeOptionsInitialized = true;
          }
        }
      },
    },
    conversionWindow: {
      immediate: false,
      async handler(n, o) {
        if (!_.isEqual(n.toString(), o.toString()) && this.selectedTab !== 'incrementality') {
          await this.debounceTableauAuthToken();
          if (this.hasAIAssistantAccess) await this.fetchAllTableauData();
        }
      },
    },
    campaignOptions: {
      immediate: false,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          if (o.length && n.length) {
            this.isCampaignOptionsInitialized = true;
          }
          if (this.isCampaignOptionsInitialized) {
            await this.debounceTableauAuthToken();
            if (this.hasAIAssistantAccess) await this.fetchAllTableauData();
          }
        }
      },
    },
    audienceOptions: {
      immediate: false,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          if (this.isAudienceOptionsInitialized) {
            await this.debounceTableauAuthToken();
            if (this.hasAIAssistantAccess) await this.fetchAllTableauData();
          }
          if (o.length) {
            this.isAudienceOptionsInitialized = true;
          }
        }
      },
    },
    creativeOptions: {
      immediate: false,
      async handler(o, n) {
        if (!_.isEqual(n, o)) {
          if (this.isCreativeOptionsInitialized) {
            await this.debounceTableauAuthToken();
            if (this.hasAIAssistantAccess) await this.fetchAllTableauData();
          }
          if (o.length) {
            this.isCreativeOptionsInitialized = true;
          }
        }
      },
    },
    eventFilters: {
      immediate: false,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          if (this.isEventOptionsInitialized) {
            await this.debounceTableauAuthToken();
            if (this.hasAIAssistantAccess) await this.fetchAllTableauData();
          }
          if (n?.eventCategory?.length !== 0) {
            this.isEventOptionsInitialized = true;
          }
        }
      },
    },
    filtersApplied: {
      deep: true,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          this.$store.set('dashboard/filtersApplied', false);
          this.$store.set('dashboard/filtersAppliedLoading', false);
          if (n && this.tableauDataParams !== this.oldTableauDataParams) {
            await this.debounceTableauAuthToken();
            if (this.hasAIAssistantAccess) await this.fetchAllTableauData();
          }
        }
      },
    },
    tableauJwtToken: {
      deep: true,
      async handler(n, o) {
        if (!_.isEqual(n, o)) {
          const tableauComponent = document.getElementById('tableauViz');
          if (!o && !this.viz) {
            await this.initializeTableau(tableauComponent);
          }
          if (this.viz) {
            this.viz.addEventListener(TableauEventType.VizLoadError, this.handleTableauError);
            // this.viz.addEventListener('firstinteractive', () => {
            //   console.log('Tableau dashboard has finished loading!');
            // });
          }
        }
      },
    },
    conversionTypeParameter: {
      immediate: false,
      async handler(n, o) {
        if (!_.isEqual(n.toString(), o.toString())) {
          await this.debounceTableauAuthToken();
          if (this.hasAIAssistantAccess) await this.fetchAllTableauData();
        }
      },
    },
    // selectedTabTitle: {
    //   deep: true,
    //   async handler(n, o) {
    //     if (!_.isEqual(n, o)) {
    //       const userHasAiAssistant = checkUserHasAiAssistant(this.currentUser);
    //       if (userHasAiAssistant) await this.fetchAllTableauData();
    //     }
    //   },
    // },
  },
  created() {
    EventBus.$on('is-chatbot-clicked', this.handleIsChatbotClicked);
    EventBus.$on('trigger-event-dropdown-close-evt', this.handleTriggerEventDropdownEventClose);
  },
  beforeDestroy() {
    EventBus.$off('is-chatbot-clicked', this.handleIsChatbotClicked);
    EventBus.$off('trigger-event-dropdown-close-evt', this.handleTriggerEventDropdownEventClose);
  },
  async mounted() {
    const reachAndFreqTabOptions = this.isReachAndFreqEnabled()
      ? REACH_AND_FREQUENCY_TAB_OPTIONS
      : [];
    this.tabOptions = [...this.tabOptions, ...reachAndFreqTabOptions];
    this.$store.dispatch(
      'common/setIsIncrementalityInMni',
      this.sectionTitle === 'Media Now Interactive // Motto Reporting'
    );
    if (EventBus.eventsLoaded) {
      const events = this.eventOptions.filter(
        (event) => event.category !== 'ctr' && event.category !== 'vcr'
      );
      this.$store.set('dashboard/filters@eventOptions', events);
    }
    // should call before tableau page loads you can replace this in place.
    this.loading = true;
    await this.fetchDsp();
    await this.handleInitTableau();
  },
  methods: {
    isReachAndFreqEnabled() {
      let enableReachAndFreq = false;
      if (this.advertiser && this.advertiser.features) {
        enableReachAndFreq = this.advertiser.features.includes(REACH_FREQ_DASHBOARD_FEATURE);
      }
      return enableReachAndFreq;
    },
    async handleInitTableau(retries = 0) {
      const maxRetries = 50;
      // maxRetries limits the number of retries to avoid infinite loops
      // campaignIds and events are important for this report so we need to wait for them to be ready
      // before initializing tableau. Testing indicates that 50 retries with 200ms delay between each retry
      // is sufficient in most cases.
      if (retries > maxRetries) {
        console.error('Max retries reached. Campaign IDs are still not ready.');
        this.loading = false;
        return;
      }
      if (EventBus.isCampaignIdsReady && EventBus.eventsLoaded) {
        await this.initTableau();
      } else {
        setTimeout(async () => {
          // retrying as campaign ids are not yet ready...
          await this.handleInitTableau(retries + 1);
        }, 200);
      }
    },
    async handleTriggerEventDropdownEventClose() {
      if (!this.isEventOptionsInitialized) {
        await this.debounceTableauAuthToken();
        if (this.hasAIAssistantAccess) await this.fetchAllTableauData();
        this.isEventOptionsInitialized = true;
      }
    },
    checkIfDemoInstance() {
      return isDemoInstance();
    },
    onTabClick(tab) {
      if (tab.disabled) {
        return;
      }
      this.selectedTab = tab.id;
      this.selectedTabHandlerType = tab.handler;
      this.selectedTabObject = tab;
      this.selectedTabTitle = tab.value;
      if (isDemoInstance()) {
        this.selectedTabUrl = tab.demoViewUrl;
        this.selectedTabSubUrl = tab.demoViewSubUrl;
        this.selectedTabDataUrl = this.getTableauDataUrls();
      } else {
        this.selectedTabUrl = tab.viewUrl;
        this.selectedTabSubUrl = tab.viewSubUrl;
        this.selectedTabDataUrl = this.getTableauDataUrls();
      }
      this.$store.set('dashboard/selectedDashboardTab', tab.id);
      if (this.selectedTabHandlerType === 'incrementalityHandler') {
        this.$store.set('dashboard/filters@ioOptions', this.campaignOptions); // incrementality widget uses ioOptions to fetch data but IoOptions are remaining empty initialy.
      }
      if (this.selectedTab !== 'incrementality') {
        this.debounceTableauAuthToken();
      }
      if (this.hasAIAssistantAccess && this.selectedTab !== 'incrementality') {
        this.fetchAllTableauData();
      }
    },
    async fetchAllTableauData() {
      if (!this.isTableauDashboardInitialized) return;
      if (!this.selectedTabDataUrl?.length) return;
      if (!EventBus.isChatbotClicked) return;
      await this.selectedTabDataUrl.forEach((item) => {
        this.fetchTableauData(item.dataUrl);
      });
    },
    async fetchTableauData(dataUrl) {
      if (!this.tableauRestToken) return {};
      const params = this.createTableauDataParams();
      const url = config.TABLEAU_URL + dataUrl + params;
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'X-Tableau-Auth': this.tableauRestToken,
          },
        });
        return response;
      } catch (err) {
        console.error('error in fetchTableauData ->', err, this.advertiser.id);
        return {};
      }
    },
    getTableauDataUrls() {
      let selectedTabDataUrl = [];
      if (config.TABLEAU_URL === TABLEAU_PROD_URL) {
        if (isDemoInstance()) {
          selectedTabDataUrl = this.selectedTabObject.demoDataUrlsProd;
        } else {
          selectedTabDataUrl = this.selectedTabObject.dataUrlsProd;
        }
      } else if (config.TABLEAU_URL === TABLEAU_DEV_URL) {
        if (isDemoInstance()) {
          selectedTabDataUrl = this.selectedTabObject.demoDataUrlsDev;
        } else {
          selectedTabDataUrl = this.selectedTabObject.dataUrlsDev;
        }
      }
      return selectedTabDataUrl;
    },
    createTableauDataParams() {
      let params = '';
      const advertiserParam = `?vf_Advertiser=${
        this.dspAdvertiserIds ? this.dspAdvertiserIds : ''
      }`;
      const startDateParam = `&vf_Start+Date=${this.startDate ? this.startDate : ''}`;
      const endDateParam = `&vf_End+Date=${this.endDate ? this.endDate : ''}`;
      const daysToConverParam = `&vf_Days+To+Convert+Parameter=${
        this.updateConversionWindow ? this.updateConversionWindow : ''
      }`;
      const eventCategoryParam = `&vf_Event+Category=${
        this.eventFilters.eventCategory ? this.eventFilters.eventCategory : ''
      }`;
      const eventSubCategoryParam = `&vf_Event+Subcategory=${
        this.eventFilters.eventSubcategory ? this.eventFilters.eventSubcategory : ''
      }`;
      // const mediaTypeParam = `&vf_Media+Type=${
      //   this.selectedMediaTypeOptions ? this.selectedMediaTypeOptions : ''
      // }`;
      const mediaTypeParam = `&vf_Media+Type+Parameter=${
        this.mediaTypeParameter ? this.mediaTypeParameter : ''
      }`;
      // const campaignIdParam = `&vf_Campaign+ID=${
      //   this.campaignOptionsValues ? this.campaignOptionsValues : ''
      // }`;
      const campaignIdParam = `&vf_Campaign+ID+Parameter=${
        this.campaignIdParameter ? this.campaignIdParameter : ''
      }`;
      // const adreadyIdParam = `&vf_AdReady+ID=${
      //   this.selectedAudienceOptions ? this.selectedAudienceOptions : ''
      // }`;
      const adreadyIdParam = `&vf_AdReady+ID+Parameter=${
        this.placementIdParameter ? this.placementIdParameter : ''
      }`;
      const creativeIdParam = `&vf_Creative+ID+Parameter=${
        this.creativeIdParameter ? this.creativeIdParameter : ''
      }`;
      const conversionTypeParam = `&vf_Conversion+Type+Parameter=${
        this.conversionTypeParameter ? this.conversionTypeParameter : ''
      }`;
      params =
        advertiserParam +
        startDateParam +
        endDateParam +
        daysToConverParam +
        eventCategoryParam +
        eventSubCategoryParam +
        mediaTypeParam +
        campaignIdParam +
        adreadyIdParam +
        creativeIdParam +
        conversionTypeParam;

      this.tableauDataParams = params;
      return this.tableauDataParams;
    },
    handleIsChatbotClicked() {
      // to call for first time only
      if (!EventBus.isChatbotClicked && this.isInitialClick) {
        EventBus.isChatbotClicked = true;
        this.fetchAllTableauData();
        this.isInitialClick = false;
      }
      EventBus.isChatbotClicked = true;
    },
  },
};
</script>
<style lang="scss" scoped>
.bdc_main_wrap.whero {
  margin-top: 19px;
  -webkit-transition: all 0.3s ease;
  -moz-transition: all 0.3s ease;
  -ms-transition: all 0.3s ease;
  -o-transition: all 0.3s ease;
  transition: all 0.3s ease;
}
.body-content.fullmax .thw_content {
  max-width: 100%;
}
.thw_content {
  max-width: 100%;
  margin: auto;
  -webkit-transition: all 0.3s ease;
  -moz-transition: all 0.3s ease;
  -ms-transition: all 0.3s ease;
  -o-transition: all 0.3s ease;
  transition: all 0.3s ease;
  padding: 0px;
  position: relative;
  z-index: 11;
}
.sopened .thw_content {
  max-width: 100%;
}
.internal-sub-nav {
  display: inline-block;
  float: none;
  margin: 11px 20px 31px 5px;
  display: block;
}
.isn-item {
  display: inline-block;
  margin-right: 20px;
  font-size: 14px;
  font-weight: 600;
  color: var(--primarydark2);
  position: relative;
  cursor: pointer;
}

.isn-item.active {
  color: var(--primarydark);
}

.isn-item.active::before {
  content: '';
  width: 100%;
  height: 2px;
  margin-top: 25px;
  position: absolute;
  background-color: var(--primarycolor);
}

.isn-item:hover {
  color: var(--primarydark);
}

.isn-item:hover::before {
  content: '';
  width: 100%;
  height: 2px;
  margin-top: 25px;
  position: absolute;
  background-color: var(--primarydark2);
}
@media (max-width: 750px) {
  .bdc_top_panel_wrap {
    flex-wrap: wrap !important;
  }
}
.bdc_top_panel_wrap {
  display: flex;
  justify-content: space-between;
  position: relative;
  top: 3;
}
.chart-sec {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
}
</style>
